import './styles/ProductDetail.scss';

import {
  Add,
  ContentCopy,
  Delete,
  Edit,
  MoreVert,
  OpenInNew,
  Share,
  WarningAmber
} from '@mui/icons-material';
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  Fab,
  Grid,
  Hidden,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers';
import { Option, Product } from '@thro-types/leihbar';

import parse from 'html-react-parser';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { useEntitlementGroupsQuery } from 'api/hooks/accessHandler';
import {
  useUnavailabilitiesQuery,
  useLenderAvailabilityQuery,
  useProductMutation,
  useOptionMutation
} from 'api/hooks/leihbar';
import { useSelfEntitlementGroupsQuery } from 'api/hooks/users/self';
import { ConfirmationDialog, Modal } from 'app/components';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { OptionModal } from '../../Options/OptionModal';
import { shouldDisableDate } from '../../date.helper';
import { canUserEdit, isLenderOfProduct } from '../../helper';
import { ProductModal } from '../ProductModal';
import { OptionDetail, ImageAndBundle, ProductInfo } from './components';
import { AvailableColorsDescription } from './components/AvailableColorsDescription';
import { CustomPickersDay } from './components/CustomPickersDay';

interface Props {
  product: Product;
  setTab: (value) => void;
}

export const ProductDetailPage = ({ product, setTab }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { permissions, user } = useAuthContext();

  const [optionRef, setOptionRef] = useState<Option | undefined>(undefined);
  const [selectedDay, setSelectedDay] = useState<Date | undefined | null>(undefined);
  const [optionModalOpen, setOptionModalOpen] = useState(false);
  const [deleteProductDialogOpen, setDeleteProductDialogOpen] = useState(false);
  const [cloneProductDialogOpen, setCloneProductDialogOpen] = useState(false);
  const [deleteOptionDialogOpen, setDeleteOptionDialogOpen] = useState(false);
  const [disabledOptionModalCancel, setDisabledOptionModalCancel] = useState(false);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const menuOpen = Boolean(anchorEl);

  const { clone, deletion } = useProductMutation();
  const { deletion: optionDeletion } = useOptionMutation(product.id);

  const { data: lenderAvailability } = useLenderAvailabilityQuery(product.lenderGroupId);

  const {
    data,
    isError,
    isLoading: isUnavailabilitiesLoading
  } = useUnavailabilitiesQuery(product.id);

  const { data: leihbarEntitlementGroups, isLoading } = useEntitlementGroupsQuery(true, {
    params: { relationType: 'LEIHBAR' }
  });

  const { data: myEntitlementGroups } = useSelfEntitlementGroupsQuery();

  const userCanEdit = canUserEdit(product, permissions, user, myEntitlementGroups);

  const handleBookingDialogOpen = () => {
    setBookingDialogOpen(true);
  };

  const handleBookingDialogClose = () => {
    setBookingDialogOpen(false);
  };

  const handleSettingsMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
    event.preventDefault();
  };
  const handleSettingsMenuClose = (event: React.MouseEvent) => {
    setAnchorEl(null);
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDelete = async (type: 'PRODUCT' | 'OPTION', id: number) => {
    if (type === 'PRODUCT') {
      try {
        await deletion.mutateAsync(id);
        dispatch(
          AlertActions.success(t(translations.pages.leihbar.product.alerts.successfulDelete()))
        );
        navigate('/leihbar');
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.product.alerts.failedDelete())));
      }
    } else if (type === 'OPTION') {
      try {
        await optionDeletion.mutateAsync(id);
        dispatch(
          AlertActions.success(t(translations.pages.leihbar.option.alerts.successfulDelete()))
        );
        setOptionRef(undefined);
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.option.alerts.failedDelete())));
      }
    } else {
      return;
    }
  };

  const handleClone = async (productId: number) => {
    try {
      const newProduct = await clone.mutateAsync(productId);
      dispatch(
        AlertActions.success(t(translations.pages.leihbar.product.alerts.successfulClone()))
      );
      navigate(`/leihbar/product/edit/${newProduct.id}`, {
        state: { clone: true }
      });
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.leihbar.product.alerts.failedClone())));
    }
  };

  const postOptionCreate = (id: number, option: Option) => {
    setOptionRef(option);
  };

  if (isError) {
    dispatch(
      AlertActions.error(t(translations.pages.leihbar.product.alerts.error.getAvailabilities()))
    );
  }

  if (isLoading) {
    return <CircularProgress />;
  }

  return (
    <Grid container className="product-detail">
      <Modal
        open={optionModalOpen}
        onClose={() => setOptionModalOpen(false)}
        headline={
          optionRef?.id
            ? t(translations.pages.leihbar.option.create.editModalTitle())
            : t(translations.pages.leihbar.option.create.createModalTitle())
        }
        disabledCancel={disabledOptionModalCancel}
      >
        <OptionModal
          closeHandler={() => setOptionModalOpen(false)}
          setDisabledCancel={setDisabledOptionModalCancel}
          option={optionRef}
          product={product}
          postOptionCreate={postOptionCreate}
        />
      </Modal>
      <ConfirmationDialog
        open={deleteProductDialogOpen}
        title={t(translations.pages.leihbar.product.dialogs.deleteConfirm.title())}
        onClose={() => {
          setDeleteProductDialogOpen(false);
        }}
        onConfirm={() => {
          handleDelete('PRODUCT', product.id);
          setDeleteProductDialogOpen(false);
        }}
      >
        <div>{t(translations.pages.leihbar.product.dialogs.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={cloneProductDialogOpen}
        title={t(translations.pages.leihbar.product.dialogs.cloneConfirm.title())}
        onClose={() => {
          setCloneProductDialogOpen(false);
        }}
        onConfirm={() => {
          handleClone(product.id);
          setCloneProductDialogOpen(false);
        }}
      >
        <div>{t(translations.pages.leihbar.product.dialogs.cloneConfirm.message())}</div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={deleteOptionDialogOpen}
        title={t(translations.pages.leihbar.option.dialogs.deleteConfirm.title())}
        onClose={() => {
          setDeleteOptionDialogOpen(false);
        }}
        onConfirm={() => {
          handleDelete('OPTION', optionRef?.id ?? 0);
          setDeleteOptionDialogOpen(false);
        }}
      >
        <div>{t(translations.pages.leihbar.option.dialogs.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      {bookingDialogOpen && (
        <ProductModal
          open={bookingDialogOpen}
          handleClose={handleBookingDialogClose}
          product={product}
        />
      )}
      <Grid container>
        <Grid container item xs={12} md={12} lg={6}>
          <ImageAndBundle product={product} setTab={setTab} />
        </Grid>
        <Divider orientation="vertical" flexItem sx={{ mr: '-1px', zIndex: 1 }} />
        <Grid item xs={12} md={12} lg={6}>
          <Grid
            item
            xs={12}
            md={12}
            className="product-detail__product"
            padding={3}
            paddingTop={userCanEdit && product.internalNote ? 0 : 2}
          >
            {/* Wird nur gesetzt wenn internalNote gesetzt ist und der Benutzer in der selben
            Domain ist */}
            {product.internalNote && isLenderOfProduct(product, myEntitlementGroups) && (
              <Alert
                icon={<WarningAmber fontSize="inherit" />}
                style={{ marginBottom: 15 }}
                severity="info"
              >
                {product.internalNote}
              </Alert>
            )}
            <Grid container justifyContent={'space-between'}>
              <Grid item xs className="product-detail__title">
                {product.name}
              </Grid>
              <Grid
                container
                item
                xs={3}
                md={2}
                lg={3}
                paddingTop={1}
                justifyContent={'space-between'}
              >
                <Grid container item spacing={2} textAlign={'right'} direction={'column'}>
                  {userCanEdit && (
                    <Grid item>
                      <Fab
                        aria-label="settings"
                        sx={theme => ({ marginX: theme.spacing(2) })}
                        size="small"
                        onClick={e => handleSettingsMenuClick(e)}
                      >
                        <MoreVert />
                      </Fab>
                      <Menu
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={handleSettingsMenuClose}
                        className="event-card__menu"
                      >
                        <MenuItem
                          onClick={e =>
                            navigate(`/leihbar/product/edit/${product.id}`, { state: { product } })
                          }
                        >
                          <ListItemIcon>
                            <Edit sx={{ color: 'lightblue' }} />
                          </ListItemIcon>
                          <ListItemText>{t(translations.common.edit())}</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => setCloneProductDialogOpen(true)}>
                          <ListItemIcon>
                            <ContentCopy sx={{ color: 'lightblue' }} />
                          </ListItemIcon>
                          <ListItemText>{t(translations.common.clone())}</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => setDeleteProductDialogOpen(true)}>
                          <ListItemIcon>
                            <Delete sx={{ color: 'red' }} />
                          </ListItemIcon>
                          <ListItemText>{t(translations.common.delete())}</ListItemText>
                        </MenuItem>
                      </Menu>
                    </Grid>
                  )}
                  <Grid item>
                    <Tooltip title={t(translations.pages.leihbar.tooltips.shareButton())}>
                      <Fab
                        size="small"
                        sx={theme => ({ marginX: theme.spacing(2) })}
                        onClick={() => {
                          navigator.clipboard.writeText(window.location.href);
                        }}
                      >
                        <Share />
                      </Fab>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                <ProductInfo
                  product={product}
                  leihBarEntitlementGroups={leihbarEntitlementGroups?.data || []}
                />
              </Grid>
              <Grid item xs alignSelf={'flex-end'} textAlign={'right'} marginTop={1}>
                <Button
                  startIcon={<OpenInNew />}
                  variant="contained"
                  className="button--create"
                  onClick={() => {
                    handleBookingDialogOpen();
                  }}
                >
                  {t(translations.pages.leihbar.selectProduct())}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} paddingY={1}>
          <Divider />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography className="product-detail__description__header">
            {t(translations.common.description())}
          </Typography>
          <div className="product-detail__description">{parse(product.description)}</div>
        </Grid>
        <Hidden mdDown>
          <Divider orientation="vertical" flexItem sx={{ mr: '-1px', zIndex: 1 }} />
        </Hidden>
        <Hidden mdUp>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Hidden>
        <Grid item xs={12} md={6}>
          <Typography className="product-detail__description__header">
            {t(translations.pages.leihbar.product.availability.header())}
          </Typography>
          <StaticDatePicker
            value={selectedDay}
            onChange={value => {
              setSelectedDay(value);
            }}
            displayStaticWrapperAs="desktop"
            renderInput={params => <TextField {...params} />}
            minDate={new Date()}
            renderDay={(day, selectedDays, pickersDayProps) => (
              <CustomPickersDay
                day={day}
                selectedDays={selectedDays}
                pickersDayProps={pickersDayProps}
                unavailabilities={data?.unavailabilities}
                articleCount={data?.articleCount}
                key={day.toString()}
              />
            )}
            loading={isLoading || isUnavailabilitiesLoading}
            shouldDisableDate={day =>
              shouldDisableDate(day, product, lenderAvailability, data?.unavailabilities, 'START')
            }
          />
          <AvailableColorsDescription />
        </Grid>
        <Grid item xs={12} paddingY={1}>
          <Divider />
        </Grid>
      </Grid>
      {(!!product.options?.length || userCanEdit) && (
        <Grid container className="product-detail__options">
          <Grid item className="product-detail__options__header" xs={12}>
            {`${t(translations.pages.leihbar.product.detailPage.options())}`}
          </Grid>
          {userCanEdit && (
            <Grid container item>
              <Grid item xs={12} className="product-detail__options__add">
                <Button
                  startIcon={<Add />}
                  variant="contained"
                  className="button--create"
                  onClick={() => {
                    setOptionRef(undefined);
                    setOptionModalOpen(true);
                  }}
                >
                  {t(translations.common.create())}
                </Button>
              </Grid>
            </Grid>
          )}
          {product.options?.map(option => (
            <Grid container item xs={12} md={6} lg={6} xl={4} key={option.id}>
              <OptionDetail
                option={option}
                handleEditClick={option => {
                  setOptionRef(option);
                  setOptionModalOpen(true);
                }}
                handleDeleteClick={option => {
                  setOptionRef(option);
                  setDeleteOptionDialogOpen(true);
                }}
                userCanEdit={userCanEdit}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Grid>
  );
};
